import { Auth0Domain } from '../Auth0Domain';
import { CDN_ROOT } from '../cdn/getCdnUrlForFile';
import { AppContext } from '../GlobalExecutionContext';

// Header spelling for names; a directive that takes no sources (upgrade-insecure-requests) is an empty list.
export type ContentSecurityPolicyDirectives = Record<string, string[]>;

export interface ContentSecurityPolicyInputs {
  appContext: AppContext;
  connectSrc: string[];
  // Only buckets the frontends fetch() presigned URLs against; top-level navigations aren't restricted by CSP.
  presignedS3BucketUrls: string[];
}

// Outside /website/, this CDN serves logos uploaded through the admin tools.
const CDN_BUNDLE_ROOT = new URL('website/', CDN_ROOT).href;

// Directory prefixes, so the rest of each vendor CDN isn't a script origin — the
// trailing slashes are what make them prefixes. Bumping the dwolla-web.js version in
// the index.html files means bumping it here.
const VENDOR_SCRIPT_URLS = [
  'https://cdn.dwolla.com/v2.1.8/',
  'https://cdn.plaid.com/link/v2/stable/',
  'https://cdn.platform.openai.com/deployments/chatkit/',
  'https://www.ladderlife.com/widget/',
];

const SENTRY_INGEST_ORIGIN = 'https://o1150834.ingest.sentry.io';

// Borrowers using Chrome's built-in page translation get the widget's toolbar and flag images
// injected into the page from these hosts; nothing in our bundle requests them.
const GOOGLE_TRANSLATE_IMAGE_HOSTS = [
  'https://translate.google.com',
  'https://fonts.gstatic.com',
  'https://www.gstatic.com',
];
const GOOGLE_TRANSLATE_STYLE_HOST = 'https://www.gstatic.com';

// script-src comes back without a nonce; each caller appends its own 'nonce-…' source.
export const buildContentSecurityPolicyDirectives = ({
  appContext,
  connectSrc,
  presignedS3BucketUrls,
}: ContentSecurityPolicyInputs): ContentSecurityPolicyDirectives => {
  const allowedUrls = [
    ...new Set([
      Auth0Domain.getForApp(appContext),
      Auth0Domain.getCustomDomainForApp(appContext),
      Auth0Domain.getForPortal(appContext),
      Auth0Domain.getCustomDomainForPortal(appContext),
      appContext === 'production' ? 'https://api.dwolla.com' : 'https://api-sandbox.dwolla.com',
    ]),
  ];

  const directives: ContentSecurityPolicyDirectives = {
    'default-src': ["'self'"],
    'base-uri': ["'self'"],
    'frame-ancestors': ["'self'"],
    'object-src': ["'none'"],
    'script-src-attr': ["'none'"],
    'script-src': ["'self'", "'report-sample'", CDN_BUNDLE_ROOT, ...VENDOR_SCRIPT_URLS],
    'connect-src': [
      "'self'",
      SENTRY_INGEST_ORIGIN,
      ...allowedUrls,
      ...connectSrc,
      ...presignedS3BucketUrls,
      'https://platform.flatfile.com',
      'https://api.x.flatfile.com',
      'wss://platform.flatfile.com',
    ],
    // Host granularity, unlike script-src: the vendor SDKs pick their own iframe URLs.
    'frame-src': [
      "'self'",
      'https://cdn.plaid.com',
      'https://cdn.platform.openai.com',
      ...allowedUrls,
      'https://platform.flatfile.com',
      'https://www.ladderlife.com',
    ],
    'form-action': ["'self'", ...allowedUrls],
    // blob: URLs can only be minted by same-origin script (logo uploader's client-side resize)
    'img-src': [
      "'self'",
      'data:',
      'blob:',
      'https://www.googletagmanager.com',
      ...GOOGLE_TRANSLATE_IMAGE_HOSTS,
      ...allowedUrls,
      CDN_ROOT,
    ],
    'style-src': [
      "'self'",
      "'unsafe-inline'",
      'data:',
      'https://fonts.googleapis.com',
      GOOGLE_TRANSLATE_STYLE_HOST,
      ...allowedUrls,
      CDN_ROOT,
    ],
    // blob: workers: auth0-spa-js token handling and Sentry Session Replay compression
    'worker-src': ["'self'", 'blob:'],
    // CDN_BUNDLE_ROOT: Vite emits font assets (e.g. monaco-editor's codicon.ttf) into the bundle
    'font-src': ["'self'", 'https://fonts.gstatic.com', 'data:', CDN_BUNDLE_ROOT],
  };

  // Local development is plain http://localhost; whether every browser exempts localhost from
  // the upgrade isn't verified, and an http-only origin gains nothing from it.
  if (appContext !== 'development' && appContext !== 'test') {
    directives['upgrade-insecure-requests'] = [];
  }

  return directives;
};

// Production stays report-only until its own soak is clean. CSP_ENFORCE overrides without a deploy.
export const isContentSecurityPolicyEnforced = (appContext: AppContext, configured: string | undefined): boolean =>
  configured === undefined ? appContext !== 'production' : configured === 'true';

export const serializeContentSecurityPolicy = (directives: ContentSecurityPolicyDirectives): string =>
  Object.entries(directives)
    .map(([name, sources]) => [name, ...sources].join(' '))
    .join('; ');
